import { User } from '~/api/models/User';
import { BaseModel } from './BaseModel';

export type SentEmailStatus = 'sending' | 'sent' | 'failed' | 'delivered' | 'bounced' | 'complained';

export const SENT_EMAIL_STATUSES: SentEmailStatus[] = ['sending', 'sent', 'failed', 'delivered', 'bounced', 'complained'];

export class SentEmail extends BaseModel {
    public id = '';
    public userId: string | null = null;
    public notification: string | null = null;
    public recipient = '';
    public subject: string | null = null;
    public status: SentEmailStatus = 'sending';
    public mailer: string | null = null;
    public templateCustomized: boolean | null = null;
    public sesMessageId: string | null = null;
    public statusMeta: Record<string, unknown> = {};
    public statusAt: string | null = null;
    public createdAt = '';
    public body: string | null = null;

    public get user(): User | undefined {
        return this.getRelationship('user');
    }

    // The backend stores the notification FQCN; only the basename is
    // meaningful to an admin (e.g. "SendPasswordReset").
    public get notificationName(): string | null {
        return this.notification ? (this.notification.split('\\').pop() ?? null) : null;
    }

    public get statusClasses(): string {
        switch (this.status) {
            case 'delivered':
                return 'bg-success-500/20 text-success-500';
            case 'bounced':
            case 'failed':
                return 'bg-danger-500/20 text-danger-500';
            case 'complained':
                return 'bg-warning-500/20 text-warning-500';
            case 'sent':
                return 'bg-blue-500/20 text-blue-300';
            default:
                return 'bg-accent-200/20 text-accent-500';
        }
    }

    // Delivery events only exist for mail that went out through the platform
    // SES account; tenant custom SMTP sends are terminal at "sent".
    public get supportsDeliveryTracking(): boolean {
        return this.mailer === 'ses';
    }

    // Admins shouldn't need to know transport internals: platform mail (SES)
    // reads as "WISP", anything else means the tenant's own SMTP handled it.
    public get mailerLabelKey(): string {
        return this.supportsDeliveryTracking ? 'admin.emails.mailers.wisp' : 'admin.emails.mailers.custom_smtp';
    }
}
